import React, { useState, useRef } from "react";
import {
    Box,
    Grid,
    Typography,
    Avatar,
    Stack,
    IconButton,
    Tooltip,
    Paper,
    Card,
    CardContent,
    Divider,
    Button,
    Chip,
    LinearProgress,
    Collapse,
    alpha,
} from "@mui/material";
import {
    Star,
    CalendarMonth,
    AccessTime,
    PlayArrow,
    Pause,
    Quiz,
    Download,
    Videocam,
    PictureAsPdf,
    ExpandMore,
    ExpandLess,
    CheckCircle,
    Warning,
    Error,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import CustomHeader from "../../components/common/CustomHeader";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { interviewApi } from "../../api/interviewApi";
import { flowwiseApi } from "../../api/flowiseApi";

/* ─── helpers ─────────────────────────────────────────────────────── */
const getScoreKey = (s) => s >= 80 ? "green" : s >= 60 ? "amber" : "red";
const getPerfLabel = (s) => s >= 80 ? "Excellent" : s >= 60 ? "Good" : s >= 40 ? "Fair" : "Needs improvement";
const getQuestionLevel = (q) => q?.level || q?.difficulty || "";

/* ─── palette ─────────────────────────────────────────────────────── */
const C = {
    green: { bg: "#EAF3DE", text: "#27500A", bar: "#639922", border: "#C0DD97" },
    amber: { bg: "#FAEEDA", text: "#633806", bar: "#BA7517", border: "#FAC775" },
    red: { bg: "#FCEBEB", text: "#791F1F", bar: "#E24B4A", border: "#F7C1C1" },
    blue: { bg: "#E6F1FB", text: "#0C447C", bar: "#185FA5", border: "#B5D4F4" },
    skyblue: { bg: "#E0F4FF", text: "#0A5A8A", bar: "#0EA5E9", border: "#7DD3FC" },
    indigo: { bg: "#EEEDFE", text: "#3C3489", bar: "#534AB7", border: "#CECBF6" },
    purple: { bg: "#F3EFFE", text: "#4A2D8F", bar: "#7C4DCC", border: "#CBAEF7" },
};

/* ─── icons ───────────────────────────────────────────────────────── */
const Icon = {
    Cal: () => <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}><rect x="2" y="3" width="12" height="12" rx="1.5" /><path d="M5 1v3M11 1v3M2 7h12" /></svg>,
    Clock: () => <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}><circle cx="8" cy="8" r="6" /><path d="M8 5v3l2 2" /></svg>,
    Check: () => <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8}><path d="M3 8l3.5 3.5L13 5" /></svg>,
    Down: () => <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8}><path d="M4 6l4 4 4-4" /></svg>,
    Up: () => <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.8}><path d="M4 10l4-4 4 4" /></svg>,
    Play: () => <svg width={11} height={11} viewBox="0 0 16 16" fill="currentColor"><path d="M5 3.5l9 4.5-9 4.5V3.5z" /></svg>,
    Pause: () => <svg width={11} height={11} viewBox="0 0 16 16" fill="currentColor"><rect x="3" y="3" width="3.5" height="10" rx="1" /><rect x="9.5" y="3" width="3.5" height="10" rx="1" /></svg>,
    Pdf: () => <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="2" y="1" width="10" height="14" rx="1.5" /><path d="M5 5h5M5 8h5M5 11h3" /><path d="M10 1v4h4" strokeLinejoin="round" /></svg>,
    Vid: () => <svg width={13} height={13} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="1" y="4" width="10" height="9" rx="1.5" /><path d="M11 6.5l4-2.5v8l-4-2.5" /></svg>,
    Quiz: () => <svg width={14} height={14} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><circle cx="8" cy="7" r="5.5" /><path d="M8 5.5c0-.8.7-1.5 1.5-1 .8.5.5 1.5-.5 2S8 8 8 8.5" /><circle cx="8" cy="11" r=".6" fill="currentColor" /></svg>,
    Spark: () => <svg width={12} height={12} viewBox="0 0 16 16" fill="currentColor"><path d="M8 1l1.5 4.5L14 7l-4.5 1.5L8 13l-1.5-4.5L2 7l4.5-1.5z" /></svg>,
    Spin: () => <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}><path d="M8 2a6 6 0 1 1-4.24 1.76" strokeLinecap="round" /></svg>,
    Eye: () => <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" /><circle cx="8" cy="8" r="2" /></svg>,
};

/* ─── GlassChip (for hero bar) ────────────────────────────────────── */
const GlassChip = ({ icon, label }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 9px", borderRadius: 20, border: "0.5px solid rgba(255,255,255,0.28)", fontSize: 11, color: "rgba(255,255,255,0.82)", background: "rgba(255,255,255,0.12)" }}>
        {icon}{label}
    </span>
);

/* ─── StatCard ─────────────────────────────────────────────────────── */
const StatCard = ({ num, label, color, accent }) => (
    <div style={{ flex: 1, background: "#fff", border: `1.5px solid ${accent}`, borderRadius: 10, padding: "12px 16px", textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 700, color, lineHeight: 1 }}>{num}</div>
        <div style={{ fontSize: 11, color: "#6b6860", marginTop: 4 }}>{label}</div>
    </div>
);

/* ─── AnsBlock ─────────────────────────────────────────────────────── */
const AnsBlock = ({ title, text, colorKey, icon, rightSlot }) => {
    const col = C[colorKey];
    return (
        <div style={{ borderRadius: 8, padding: "11px 13px", background: col.bg, border: `0.5px solid ${col.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", color: col.text }}>
                    {icon}{title}
                </div>
                {rightSlot}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: "#1a1916" }}>{text}</div>
        </div>
    );
};

/* ─── ConsolationModal ─────────────────────────────────────────────── */
const ConsolationModal = ({ name, message, onClose }) => (
    <div
        style={{ position: "fixed", inset: 0, background: "rgba(10,20,40,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1200, padding: 20 }}
        onClick={onClose}
    >
        <div
            style={{ background: "#fff", borderRadius: 16, padding: "24px 24px 20px", maxWidth: 500, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.18)" }}
            onClick={e => e.stopPropagation()}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.indigo.bg, border: `1.5px solid ${C.indigo.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.indigo.bar, flexShrink: 0 }}>
                    <Icon.Spark />
                </div>
                <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1916" }}>Reasoning for {name}</div>
                    <div style={{ fontSize: 11, color: "#6b6860", marginTop: 1 }}>AI explanation of score</div>
                </div>
            </div>
            <div style={{ background: C.indigo.bg, border: `0.5px solid ${C.indigo.border}`, borderRadius: 10, padding: "14px 16px", fontSize: 13, lineHeight: 1.75, color: C.indigo.text }}>
                {message}
            </div>
            <button
                onClick={onClose}
                style={{ marginTop: 14, width: "100%", padding: "9px 0", borderRadius: 8, border: "none", background: C.indigo.bar, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}
            >
                Close
            </button>
        </div>
    </div>
);

/* ══════════════════════════════════════════════════════════════════════
   MAIN
══════════════════════════════════════════════════════════════════════ */
const InterviewReport = () => {
    const location = useLocation();
    const { state } = location || {};
    const data = state?.report || {};
    const candidatePhoto = data?.candidate_photo || "";

    const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "" });
    const [playingId, setPlayingId] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [downloading, setDownloading] = useState({ report: false, video: false });
    const [consolation, setConsolation] = useState({ loading: {}, text: {}, modal: null });
    const audioRefs = useRef({});

    const showSnackbar = (message, type) => {
        setSnackbar({ open: true, message, type });
    };

    const toggleQuestion = (index) => {
        setExpanded(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    /* -------------------------------------------------
        AUDIO PLAY / PAUSE
    --------------------------------------------------*/
    const handleAudioToggle = (id, audioUrl) => {
        try {
            // Pause all other audio elements
            Object.values(audioRefs.current).forEach((audio) => {
                if (audio && audio !== audioRefs.current[id]) {
                    audio.pause();
                }
            });

            const audio = audioRefs.current[id];
            if (!audio) return;

            if (playingId === id) {
                // If currently playing, pause it
                audio.pause();
                setPlayingId(null);
            } else {
                // Set the source to the presigned URL from backend
                audio.src = audioUrl;
                audio.play();
                setPlayingId(id);
            }
        } catch (error) {
            console.error("Error toggling audio:", error);
            showSnackbar("Failed to play audio", "error");
        }
    };

    const handleConsolation = async (q, i) => {

        // ✅ If already fetched → just open modal
        if (consolation.text[i]) {
            setConsolation(prev => ({ ...prev, modal: i }));
            return;
        }

        // ✅ Set loading
        setConsolation(prev => ({
            ...prev,
            loading: { ...prev.loading, [i]: true }
        }));

        try {
            const response = await flowwiseApi.getConsolation({
                id: q.id, // 🔥 IMPORTANT (for DB lookup)
                ideal_answer: q.ideal_answer,
                candidate_answer: q.candidate_answer,
                score: q.score,
            });

            const { status, data, message } = response || {};

            // ❌ API error
            if (status !== 200) {
                showSnackbar(message || "Failed to generate reasoning.", "error");
                return;
            }

            // ✅ Extract reasoning (NOT consolation)
            const text = data?.reasoning || "No reasoning available.";

            // ✅ Save + open modal
            setConsolation(prev => ({
                ...prev,
                text: { ...prev.text, [i]: text },
                modal: i
            }));

        } catch (error) {
            console.error("Reasoning error:", error);
            showSnackbar("Error generating reasoning.", "error");

        } finally {
            // ✅ Stop loading
            setConsolation(prev => ({
                ...prev,
                loading: { ...prev.loading, [i]: false }
            }));
        }
    };

    /* -------------------------------------------------
        DOWNLOAD REPORT
    --------------------------------------------------*/
    const handleDownloadReport = async () => {
        if (!data?.interview_id) { showSnackbar("Invalid interview reference.", "warning"); return; }
        setDownloading(p => ({ ...p, report: true }));
        try {
            const response = await flowwiseApi.downloadReport({
                interview: data.interview_id,
            });
            const { status, data: fileBlob, message } = response || {};
            if (status === 0) { showSnackbar("Network error.", "warning"); return; }
            if (status >= 400) { showSnackbar(message || "Failed to download report.", "error"); return; }
            if (status === 200 && fileBlob instanceof Blob) {
                const url = window.URL.createObjectURL(fileBlob);
                const a = Object.assign(document.createElement("a"), { href: url, download: `Interview_Report_${data.id}.pdf` });
                document.body.appendChild(a); a.click(); document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
                showSnackbar("Report downloaded.", "success"); return;
            }
            showSnackbar("Unexpected response.", "warning");
        } catch { showSnackbar("Something went wrong.", "error"); }
        finally { setDownloading(p => ({ ...p, report: false })); }
    };

    const handleDownloadVideo = async () => {
        if (!data?.interview_id) {
            showSnackbar("Invalid interview reference.", "warning");
            return;
        }

        setDownloading(prev => ({
            ...prev,
            video: true
        }));

        try {
            const response = await interviewApi.getVideoPresignedUrl({
                interview_id: data.interview_id
            });

            // console.log("Video download response:", response);

            const {
                status,
                message,
                data: responseData
            } = response || {};

            if (status === 0) {
                showSnackbar(
                    message || "Network error. Please try again.",
                    "warning"
                );
                return;
            }

            if (status === 400) {
                showSnackbar(
                    message || "Invalid interview reference.",
                    "warning"
                );
                return;
            }

            if (status === 404) {
                showSnackbar(
                    message || "Interview recording not found.",
                    "warning"
                );
                return;
            }

            if (status >= 500) {
                showSnackbar(
                    message || "Unable to generate download link.",
                    "error"
                );
                return;
            }

            const videoUrl = responseData?.video_url;

            // KEEPING YOUR EXISTING LOGIC
            if (status === 200 && videoUrl) {
                const a = Object.assign(document.createElement("a"), {
                    href: videoUrl,
                    download: `Interview_Recording_${data.id}.webm`
                });

                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);

                showSnackbar("Recording download started.", "success");
                return;
            }

            showSnackbar(
                message || "Video link unavailable.",
                "warning"
            );

        } catch (error) {
            console.error("Video download error:", error);

            showSnackbar(
                "Something went wrong.",
                "error"
            );
        } finally {
            setDownloading(prev => ({
                ...prev,
                video: false
            }));
        }
    };

    /* derived */
    const qs = data.interview_QA || [];
    const avg = qs.length ? (qs.reduce((s, q) => s + (q.score || 0), 0) / qs.length) : 0;
    const excellent = qs.filter(q => q.score >= 80).length;
    const initials = (data.full_name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
    const overallC = getScoreKey(avg);
    const bestAnswer = qs.find(q => q.audio_reasoning != null && q.audio_reasoning !== "")
    const confidenceScore = bestAnswer?.confidence_score ?? null;
    const fluencyScore = bestAnswer?.fluency_score ?? null;
    const commScore = bestAnswer?.communication_score ?? null;
    const audioReasoning = bestAnswer?.audio_reasoning ?? null;

    return (
        <div style={{ minHeight: "91.2vh", background: "#eef2f7", fontFamily: "'DM Sans','Inter',sans-serif" }}>
            <CustomHeader title="Candidate Report" />

            {/* ════════ HERO — edge-to-edge, no side padding ════════ */}
            <div style={{
                background: "linear-gradient(135deg, #0d2340 0%, #103f6e 55%, #1a5fa8 100%)",
                padding: "22px 24px 64px",
                position: "relative",
                overflow: "hidden",
            }}>
                {/* decorative rings */}
                <div style={{ position: "absolute", top: -60, right: -60, width: 220, height: 220, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", top: -30, right: -30, width: 140, height: 140, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.06)", pointerEvents: "none" }} />
                <div style={{ position: "absolute", bottom: -80, left: "30%", width: 300, height: 300, borderRadius: "50%", background: "rgba(255,255,255,0.03)", pointerEvents: "none" }} />

                <div style={{ maxWidth: 860, margin: "0 auto", position: "relative" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>

                        {/* avatar + info */}
                        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                            <div style={{ width: 140, height: 140, borderRadius: "50%", flexShrink: 0, background: "rgba(255,255,255,0.15)", border: "3px solid rgba(255,255,255,0.45)", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22, color: "#fff" }}>
                                {candidatePhoto
                                    ? <img src={candidatePhoto} alt="Candidate" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    : initials
                                }
                            </div>

                            <div>
                                <div style={{ fontSize: 17, fontWeight: 700, color: "#fff", lineHeight: 1.2 }}>{data.full_name || "Candidate Name"}</div>
                                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 3 }}>{data.email || "Email not available"}</div>
                                <div style={{ display: "flex", gap: 6, marginTop: 9, flexWrap: "wrap" }}>
                                    <GlassChip icon={<Icon.Cal />} label={new Date(data.completed_at || data.created_at || new Date()).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} />
                                </div>
                            </div>
                        </div>

                        {/* score + action buttons */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
                            <div style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 12, padding: "12px 22px", textAlign: "center" }}>
                                <div style={{ fontSize: 32, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{avg.toFixed(2)}%</div>
                                <div style={{ marginTop: 7, display: "inline-block", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: C[overallC].bg, color: C[overallC].text }}>
                                    {getPerfLabel(avg)}
                                </div>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                                {data.interview_id && (
                                    <button
                                        onClick={handleDownloadReport}
                                        disabled={downloading.report}
                                        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", cursor: "pointer", background: "rgba(255,255,255,0.92)", color: C.blue.bar, border: "none" }}
                                    >
                                        <Icon.Pdf />{downloading.report ? "Downloading…" : "PDF report"}
                                    </button>
                                )}
                                <button
                                    onClick={handleDownloadVideo}
                                    disabled={downloading.video}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap", cursor: "pointer", background: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.4)" }}
                                >
                                    <Icon.Vid />{downloading.video ? "Downloading…" : "Recording"}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* ════════ CONTENT — overlaps hero bottom ════════ */}
            <div style={{ maxWidth: 860, margin: "-40px auto 0", padding: "0 24px 40px", position: "relative" }}>

                {/* stat cards */}
                {qs.length > 0 && (
                    <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                        <StatCard num={qs.length} label="Total questions" color={C.blue.bar} accent={C.blue.border} />
                        <StatCard num={excellent} label="Excellent answers" color={C.green.bar} accent={C.green.border} />
                        <StatCard num={`${avg.toFixed(2)}%`} label="Average score" color={C.blue.text} accent={C.blue.border} />
                    </div>
                )}

                {/* section label */}
                {/* ── Communication Analysis Summary Card ── */}
                {confidenceScore != null && (
                    <div style={{
                        background: "#fff",
                        border: `1px solid ${C.purple.border}`,
                        borderRadius: 10,
                        overflow: "hidden",
                        marginBottom: 14,
                    }}>
                        {/* card header */}
                        <div style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "10px 14px",
                            background: `linear-gradient(135deg, #F3EFFE 0%, #faf8ff 100%)`,
                            borderBottom: `0.5px solid ${C.purple.border}`,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: C.purple.text }}>
                                <svg width={12} height={12} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><rect x="5" y="1" width="6" height="9" rx="3" /><path d="M2 8a6 6 0 0 0 12 0M8 14v2" /></svg>
                                Soft Skills Analysis
                            </div>
                            <span style={{ fontSize: 10, color: C.purple.bar, fontWeight: 600, background: "rgba(124,77,204,0.1)", padding: "2px 8px", borderRadius: 20 }}>
                                Based on Candidate's Audio
                            </span>
                        </div>

                        {/* score bars */}
                        <div style={{ padding: "14px", display: "flex", flexDirection: "column", gap: 12 }}>
                            {[
                                { label: "Confidence", value: confidenceScore, icon: <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M8 2l1.5 4H14l-3.5 2.5L12 13 8 10l-4 3 1.5-4.5L2 6h4.5z" /></svg> },
                                { label: "Fluency", value: fluencyScore, icon: <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M2 8h2l2-4 2 8 2-4 2 2h2" /></svg> },
                                { label: "Communication", value: commScore, icon: <svg width={11} height={11} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5}><path d="M2 2h12v9H9l-3 3v-3H2z" /></svg> },
                            ].map(({ label, value, icon }) => {
                                const pct = Math.round(value ?? 0);
                                const colKey = getScoreKey(pct);
                                const col = C[colKey];
                                return (
                                    <div key={label} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: "#4a4f5a" }}>
                                                <span style={{ color: C.purple.bar }}>{icon}</span>
                                                {label}
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                <span style={{ fontSize: 11, color: "#6b6860" }}>{getPerfLabel(pct)}</span>
                                                <span style={{ fontSize: 11, fontWeight: 700, color: col.text, background: col.bg, border: `0.5px solid ${col.border}`, padding: "1px 8px", borderRadius: 20 }}>
                                                    {pct}%
                                                </span>
                                            </div>
                                        </div>
                                        <div style={{ height: 5, background: "#e8eaf0", borderRadius: 3, overflow: "hidden" }}>
                                            <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, ${col.bar}99, ${col.bar})`, transition: "width .5s ease" }} />
                                        </div>
                                    </div>
                                );
                            })}

                            {/* audio reasoning */}
                            {audioReasoning && (
                                <div style={{
                                    marginTop: 2,
                                    padding: "10px 12px",
                                    borderRadius: 8,
                                    background: "rgba(124,77,204,0.06)",
                                    border: `0.5px solid ${C.purple.border}`,
                                    fontSize: 12,
                                    lineHeight: 1.75,
                                    color: C.purple.text,
                                }}>
                                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 5, opacity: 0.7 }}>
                                        AI Reasoning
                                    </div>
                                    {audioReasoning}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* section label */}
                <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, fontWeight: 700, color: "#6b6860", letterSpacing: ".07em", textTransform: "uppercase", marginBottom: 10 }}>
                    <Icon.Quiz /> Question analysis
                </div>

                {/* question cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {qs.length ? qs.map((q, i) => {
                        const cKey = getScoreKey(q.score);
                        const isOpen = !!expanded[i];
                        const loading = !!consolation.loading[i];
                        const fetched = !!consolation.text[i];
                        const skill = q.skill || q.skill_cluster || "General";
                        const level = getQuestionLevel(q);

                        return (
                            <div key={i} style={{ background: "#fff", border: "0.5px solid #dde2ea", borderRadius: 10, overflow: "hidden" }}>

                                {/* header row */}
                                <div
                                    style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", cursor: "pointer" }}
                                    onClick={() => toggleQuestion(i)}
                                >
                                    <span style={{ flexShrink: 0, background: C.blue.bg, color: C.blue.text, fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 5, fontFamily: "monospace", marginTop: 1 }}>
                                        Q{i + 1}
                                    </span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.5, color: "#1a1916" }}>
                                            {q.question}
                                        </div>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 8 }}>
                                            <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: C.blue.bg, color: C.blue.text, border: `0.5px solid ${C.blue.border}` }}>
                                                Skill: {skill}
                                            </span>
                                            {level && (
                                                <span style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 700, background: C.indigo.bg, color: C.indigo.text, border: `0.5px solid ${C.indigo.border}` }}>
                                                    Level: {level}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{ padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: C[cKey].bg, color: C[cKey].text }}>
                                            {q.score}
                                        </span>
                                        <div style={{ width: 22, height: 22, border: "0.5px solid #dde2ea", borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center", color: "#6b6860", flexShrink: 0 }}>
                                            {isOpen ? <Icon.Up /> : <Icon.Down />}
                                        </div>
                                    </div>
                                </div>

                                {/* progress bar */}
                                <div style={{ padding: "0 14px 10px", display: "flex", alignItems: "center", gap: 8 }}>
                                    <div style={{ flex: 1, height: 3, background: "#e8eaf0", borderRadius: 2, overflow: "hidden" }}>
                                        <div style={{ height: "100%", borderRadius: 2, width: `${q.score}%`, background: C[cKey].bar, transition: "width .4s ease" }} />
                                    </div>
                                    <span style={{ fontSize: 10, color: "#a0a8b8", fontFamily: "monospace", width: 28, textAlign: "right" }}>{q.score}%</span>
                                </div>

                                {/* expanded body */}
                                {isOpen && (
                                    <div style={{ borderTop: "0.5px solid #e8eaf0", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>

                                        <AnsBlock
                                            title="Ideal answer"
                                            text={q.ideal_answer}
                                            colorKey="green"
                                            icon={<Icon.Check />}
                                        />

                                        <AnsBlock
                                            title="Candidate answer"
                                            text={q.candidate_answer}
                                            colorKey="skyblue"
                                            icon={<Icon.Check />}
                                            rightSlot={q.audio ? (
                                                <>
                                                    <button
                                                        style={{ width: 26, height: 26, borderRadius: "50%", background: C.blue.bar, color: "#fff", border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
                                                        onClick={e => { e.stopPropagation(); handleAudioToggle(i, q.audio); }}
                                                    >
                                                        {playingId === i ? <Icon.Pause /> : <Icon.Play />}
                                                    </button>
                                                    <audio
                                                        ref={el => audioRefs.current[i] = el}
                                                        id={`audio-${i}`}
                                                        src={q.audio}
                                                        onEnded={() => setPlayingId(null)}
                                                    />
                                                </>
                                            ) : null}
                                        />

                                        {/* give consolation button */}
                                        <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 2 }}>
                                            <button
                                                onClick={() => handleConsolation(q, i)}
                                                disabled={loading}
                                                style={{
                                                    display: "inline-flex", alignItems: "center", gap: 6,
                                                    padding: "7px 14px", borderRadius: 8,
                                                    fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                                                    cursor: loading ? "default" : "pointer",
                                                    opacity: loading ? 0.7 : 1,
                                                    transition: "opacity .15s",
                                                    ...(fetched
                                                        ? { background: C.indigo.bg, color: C.indigo.text, border: `1px solid ${C.indigo.border}` }
                                                        : { background: C.indigo.bar, color: "#fff", border: "none" }
                                                    ),
                                                }}
                                            >
                                                {loading
                                                    ? <><Icon.Spin /> Generating…</>
                                                    : fetched
                                                        ? <><Icon.Eye />   View reasoning</>
                                                        : <><Icon.Spark /> Give reasoning</>
                                                }
                                            </button>
                                        </div>

                                    </div>
                                )}
                            </div>
                        );
                    }) : (
                        <div style={{ background: "#fff", border: "0.5px solid #dde2ea", borderRadius: 10, padding: 36, textAlign: "center", color: "#6b6860", fontSize: 13 }}>
                            No question data available.
                        </div>
                    )}
                </div>
            </div>

            {/* consolation modal */}
            {consolation.modal !== null && consolation.text[consolation.modal] && (
                <ConsolationModal
                    name={data.full_name || "the candidate"}
                    message={consolation.text[consolation.modal]}
                    onClose={() => setConsolation(p => ({ ...p, modal: null }))}
                />
            )}

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                type={snackbar.type}
                onClose={() => setSnackbar(p => ({ ...p, open: false }))}
            />
        </div>
    );
};

export default InterviewReport;
